import { GENERIC } from '../variant.js';
import { alignmentToCues, visemeAt } from './phonemes.js';
import { forSpeech } from './prosody.js';
import type { SpeakHandlers, VoiceEngine } from './voice.js';

/**
 * Voz de produccion.
 *
 * Pide el audio al propio servidor (que a su vez habla con ElevenLabs, para que
 * la clave nunca llegue al navegador) y recibe, junto al audio, el instante
 * exacto en que se pronuncia cada caracter.
 *
 * Eso cambia la calidad de la sincronizacion labial: con la voz del navegador
 * hay que estimar el ritmo y corregirlo sobre la marcha; aqui se conoce de
 * antemano, asi que la boca va exactamente con el sonido por larga que sea la
 * frase. Ademas se mide el volumen real de la onda, de modo que la boca se abre
 * mas en las silabas fuertes y menos en las debiles.
 */

interface VoicePayload {
  audio: string;
  chars: string[];
  startsMs: number[];
  durationsMs: number[];
}

/** Decodifica base64 a bytes sin pasar por cadenas intermedias gigantes. */
function toBytes(base64: string): ArrayBuffer {
  const binary = atob(base64);
  const bytes = new Uint8Array(binary.length);
  for (let i = 0; i < binary.length; i += 1) bytes[i] = binary.charCodeAt(i);
  return bytes.buffer;
}

export class ElevenLabsEngine implements VoiceEngine {
  readonly name = 'elevenlabs';

  private context: AudioContext | null = null;
  private analyser: AnalyserNode | null = null;
  private source: AudioBufferSourceNode | null = null;
  private frame = 0;
  private cancelled = false;
  /** Audio ya pedido, por texto: la frase siguiente se descarga mientras suena esta. */
  private pending = new Map<string, Promise<VoicePayload>>();

  private request(spoken: string): Promise<VoicePayload> {
    let job = this.pending.get(spoken);
    if (!job) {
      job = fetch('/api/voice', {
        method: 'POST',
        headers: { 'content-type': 'application/json' },
        // En la version generica, otra voz: nunca el clon del Dr.
        body: JSON.stringify(GENERIC ? { text: spoken, voice: 'generico' } : { text: spoken }),
      }).then(async (response) => {
        if (!response.ok) throw new Error(String(response.status));
        return (await response.json()) as VoicePayload;
      });
      job.catch(() => undefined); // si nadie la llega a pedir, que no quede un rechazo suelto
      this.pending.set(spoken, job);
    }
    return job;
  }

  prefetch(text: string): void {
    const spoken = forSpeech(text);
    if (spoken.trim()) void this.request(spoken);
  }

  voiceName(): string | null {
    return 'ElevenLabs';
  }

  available(): boolean {
    return audioContextClass() !== null;
  }

  /**
   * En iOS el AudioContext nace suspendido y SOLO puede reanudarse dentro de un
   * gesto del usuario. Si se omite esto, todo funciona salvo que no se oye nada.
   */
  unlock(): void {
    if (!this.available()) return;
    this.context ??= new (audioContextClass()!)();
    if (this.context.state === 'suspended') void this.context.resume();
  }

  async speak(text: string, handlers: SpeakHandlers): Promise<void> {
    this.cancelled = false;
    const spoken = forSpeech(text);
    if (!spoken.trim()) return;

    let payload: VoicePayload;
    try {
      payload = await this.request(spoken);
      this.pending.delete(spoken);
    } catch {
      this.pending.delete(spoken);
      // Sin voz de produccion no se deja al avatar mudo y quieto: se devuelve
      // el control para que el motor de respaldo vocalice la frase.
      throw new Error('voice-unavailable');
    }

    if (this.cancelled) return;

    this.context ??= new (audioContextClass()!)();
    const context = this.context;
    if (context.state === 'suspended') await context.resume();

    const buffer = await context.decodeAudioData(toBytes(payload.audio));
    if (this.cancelled) return;

    const analyser = context.createAnalyser();
    analyser.fftSize = 256;
    analyser.smoothingTimeConstant = 0.72;
    this.analyser = analyser;

    const source = context.createBufferSource();
    source.buffer = buffer;
    source.connect(analyser);
    analyser.connect(context.destination);
    this.source = source;

    const cues = alignmentToCues(payload.chars, payload.startsMs, payload.durationsMs);
    const samples = new Uint8Array(analyser.frequencyBinCount);

    return new Promise<void>((resolve) => {
      let settled = false;
      const finish = () => {
        if (settled) return;
        settled = true;
        cancelAnimationFrame(this.frame);
        handlers.onViseme('REST');
        handlers.onAmplitude(0);
        this.source = null;
        this.analyser = null;
        resolve();
      };

      // El reloj es el del propio AudioContext, no el del navegador: es el
      // unico que va realmente a la par del sonido que se esta oyendo.
      const startedAt = context.currentTime;
      handlers.onTrack(cues, performance.now());
      // Aviso para herramientas (grabacion del video de entrega): cuando
      // empieza a sonar cada frase. No tiene ningun otro uso.
      window.dispatchEvent(new CustomEvent('avatar:speak', { detail: { text: spoken, at: Date.now() } }));

      const tick = () => {
        this.frame = requestAnimationFrame(tick);
        const elapsed = (context.currentTime - startedAt) * 1000;

        handlers.onViseme(visemeAt(cues, elapsed));

        // Volumen instantaneo (RMS) de la onda que suena ahora mismo.
        analyser.getByteTimeDomainData(samples);
        let sum = 0;
        for (const sample of samples) {
          const centred = (sample - 128) / 128;
          sum += centred * centred;
        }
        const rms = Math.sqrt(sum / samples.length);
        handlers.onAmplitude(Math.min(1, rms * 3.2));
      };

      source.onended = finish;
      source.start();
      this.frame = requestAnimationFrame(tick);
    });
  }

  cancel(): void {
    this.pending.clear();
    this.cancelled = true;
    cancelAnimationFrame(this.frame);
    if (this.source) {
      try {
        this.source.stop();
      } catch {
        /* ya habia terminado */
      }
      this.source = null;
    }
  }
}

/** Pregunta al servidor si la voz de produccion esta configurada. */
export async function productionVoiceAvailable(): Promise<boolean> {
  try {
    const response = await fetch('/api/voice/status');
    if (!response.ok) return false;
    const status = (await response.json()) as { available?: boolean };
    return status.available === true;
  } catch {
    return false;
  }
}

/**
 * Safari anterior a iOS 14.5 solo expone el prefijo webkitAudioContext. Sin
 * este respaldo, esos iPhone se quedarian sin la voz de produccion.
 */
function audioContextClass(): typeof AudioContext | null {
  if (typeof window === 'undefined') return null;
  const w = window as unknown as { AudioContext?: typeof AudioContext; webkitAudioContext?: typeof AudioContext };
  return w.AudioContext ?? w.webkitAudioContext ?? null;
}
